import * as React from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '../lib/utils.js';

export type ToastTone = 'default' | 'success' | 'warning' | 'destructive' | 'info';

export interface ToastItem {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  tone: ToastTone;
  /** ミリ秒。0 で自動消滅しない。 */
  duration: number;
}

type ToastInput = Omit<Partial<ToastItem>, 'id' | 'title'> & { title: React.ReactNode };

interface ToastContextValue {
  toasts: ToastItem[];
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  /** よく使う3種のショートハンド。 */
  success: (title: React.ReactNode, description?: React.ReactNode) => string;
  error: (title: React.ReactNode, description?: React.ReactNode) => string;
  info: (title: React.ReactNode, description?: React.ReactNode) => string;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

let seq = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const timers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = React.useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = React.useCallback(
    (input: ToastInput) => {
      const id = `t${++seq}`;
      const item: ToastItem = {
        id,
        title: input.title,
        description: input.description,
        tone: input.tone ?? 'default',
        duration: input.duration ?? (input.tone === 'destructive' ? 8000 : 4500),
      };
      setToasts((prev) => [...prev, item]);
      if (item.duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), item.duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  React.useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const value = React.useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (title, description) => push({ title, description, tone: 'success' }),
      error: (title, description) => push({ title, description, tone: 'destructive' }),
      info: (title, description) => push({ title, description, tone: 'info' }),
    }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast は <ToastProvider> の内側でのみ使えます');
  return ctx;
}

const TONE_ICON = {
  default: Info,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
} as const;

const TONE_CLASS: Record<ToastTone, string> = {
  default: 'border-border [&_[data-icon]]:text-muted-foreground',
  info: 'border-info/40 [&_[data-icon]]:text-info',
  success: 'border-success/40 [&_[data-icon]]:text-success',
  warning: 'border-warning/40 [&_[data-icon]]:text-warning',
  destructive: 'border-destructive/40 [&_[data-icon]]:text-destructive',
};

function Toaster() {
  const { toasts, dismiss } = useToast();
  if (toasts.length === 0) return null;
  /*
   * `print:hidden`: トーストは紙に要らない。しかも fixed の要素は印刷の**全ページ**に
   * 繰り返し刷られる（AppShell は印刷時に高さの固定を外すので、長い画面ほど枚数が増える）。
   */
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 print:hidden"
      role="region"
      aria-label="通知"
    >
      {toasts.map((t) => {
        const Icon = TONE_ICON[t.tone];
        return (
          <div
            key={t.id}
            role="status"
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-lg border bg-card p-3 pr-2 text-card-foreground shadow-2xl shadow-black/40 animate-slide-in-right',
              TONE_CLASS[t.tone],
            )}
          >
            <Icon data-icon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium leading-snug">{t.title}</p>
              {t.description ? (
                <p className="text-xs leading-snug text-muted-foreground">{t.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
              aria-label="閉じる"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
